import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiArrowLeftLine,
    RiSearchLine,
    RiFilterLine
} from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminMaterial } from '@/hooks/admin/useAdmin';
import { useMaterials, useDeleteMaterial } from '@/hooks/admin/useAdminMaterials';
import MaterialModal from '@/modals/admin/MaterialModal';

const MaterialsAdmin: React.FC = () => {
    const [selectedMaterial, setSelectedMaterial] = useState<AdminMaterial | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<string>('all');

    const { data: materials = [], isLoading, error } = useMaterials();
    const deleteMaterial = useDeleteMaterial();

    const filteredMaterials = materials.filter(material => {
        const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            material.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterActive === 'all' ||
                            (filterActive === 'active' && material.is_active) ||
                            (filterActive === 'inactive' && !material.is_active);
        return matchesSearch && matchesFilter;
    });

    const handleCreateMaterial = () => {
        setSelectedMaterial(undefined);
        setModalOpen(true);
    };

    const handleEditMaterial = (material: AdminMaterial) => {
        setSelectedMaterial(material);
        setModalOpen(true);
    };

    const handleDeleteMaterial = async (material: AdminMaterial) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar el material "${material.name}"?`)) {
            try {
                await deleteMaterial.mutateAsync(material.id);
            } catch (error) {
                console.error('Error deleting material:', error);
                alert('Error al eliminar el material');
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedMaterial(undefined);
    };

    const handleModalSuccess = () => {
        // Modal will close itself on success
    };

    if (isLoading) {
        return (
            <div className="admin-page-loading">
                <Spinner size="lg" />
                <p>Cargando materiales...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-error">
                <p>Error al cargar los materiales</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="admin-page-header-left">
                    <Link to="/admin" className="admin-back-link">
                        <RiArrowLeftLine />
                        <span>Volver al Dashboard</span>
                    </Link>
                    <div>
                        <h1 className="admin-page-title">Gestión de Materiales</h1>
                        <p className="admin-page-subtitle">
                            Administra los materiales disponibles para los productos
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreateMaterial}
                    variant="primary"
                    className="admin-create-btn"
                >
                    <RiAddLine />
                    Nuevo Material
                </Button>
            </div>

            {/* Filters and Search */}
            <div className="admin-filters">
                <div className="admin-search">
                    <Input
                        name="search"
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder="Buscar materiales..."
                        startContent={<RiSearchLine />}
                    />
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        <RiFilterLine />
                        Estado:
                    </label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </select>
                </div>
            </div>

            {/* Materials Table */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Descripción</th>
                            <th>Costo Adicional</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMaterials.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="admin-table-empty">
                                    {searchTerm || filterActive !== 'all'
                                        ? 'No se encontraron materiales con los filtros aplicados'
                                        : 'No hay materiales registrados'
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredMaterials.map(material => (
                                <tr key={material.id}>
                                    <td className="admin-table-name">
                                        {material.name}
                                    </td>
                                    <td className="admin-table-description">
                                        {material.description ? (
                                            <span title={material.description}>
                                                {material.description.length > 50
                                                    ? `${material.description.substring(0, 50)}...`
                                                    : material.description
                                                }
                                            </span>
                                        ) : (
                                            <span className="admin-no-description">Sin descripción</span>
                                        )}
                                    </td>
                                    <td className="admin-table-price">
                                        ${material.additional_cost.toFixed(2)}
                                    </td>
                                    <td>
                                        <span className={`admin-status ${material.is_active ? 'active' : 'inactive'}`}>
                                            {material.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="admin-table-actions">
                                        <Button
                                            onClick={() => handleEditMaterial(material)}
                                            variant="secondary"
                                            size="sm"
                                            className="admin-action-btn"
                                        >
                                            <RiEditLine />
                                            Editar
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteMaterial(material)}
                                            variant="danger"
                                            size="sm"
                                            className="admin-action-btn"
                                            disabled={deleteMaterial.isPending}
                                        >
                                            <RiDeleteBinLine />
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Material Modal */}
            <MaterialModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                material={selectedMaterial}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default MaterialsAdmin;
